import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  IconButton,
  Icon,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useGetDailyFormInspectionQuestions from "hooks/fleet/useGetDailyFormInspectionQuestions";
import { capitalCase } from "change-case";
import { DailyFormInspectionQuestion, DailyFormInspectionQuestionType } from "generated/graphql";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect/MDSelect";
import useUpdateDailyFormInspectionQuestion from "hooks/fleet/useUpdateDailyFormInspectionQuestion";
import useDeleteDailyFormInspectionQuestion from "hooks/fleet/useDeleteDailyFormInspectionQuestion";
import useConfirmationDialog from "hooks/useConfirmationDialog";
import ActionConfirmDialog from "components/ActionConfirmDialog/ActionConfirmDialog";

function QuestionsList() {
  const [getDailyFormInspectionQuestions, { data: questions, loading: isLoadingQuestions }] =
    useGetDailyFormInspectionQuestions();

  useEffect(() => {
    getDailyFormInspectionQuestions();
  }, [getDailyFormInspectionQuestions]);

  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [editFormState, setEditFormState] = useState({
    question: "",
    type: DailyFormInspectionQuestionType.BOOLEAN,
  });
  const [questionToDelete, setQuestionToDelete] = useState<any>(null);

  const [updateQuestion, { loading: isUpdatingQuestion }] = useUpdateDailyFormInspectionQuestion();
  const [deleteQuestion] = useDeleteDailyFormInspectionQuestion();

  const deleteConfirmation = useConfirmationDialog({
    acceptFn: async () => {
      await deleteQuestion({ variables: { id: questionToDelete.id } });
      setQuestionToDelete(null);
      return true;
    },
    cancelFn: async () => {
      setQuestionToDelete(null);
      return true;
    },
  });

  const handleStartEditing = (question: any) => {
    setEditingQuestionId(question.id);
    setEditFormState({
      question: question.question,
      type: question.type,
    });
  };

  const handleCancelEditing = () => {
    setEditingQuestionId(null);
  };

  const handleSaveChanges = async () => {
    if (!editingQuestionId) return;
    await updateQuestion({
      variables: {
        input: {
          id: editingQuestionId,
          question: editFormState.question,
          type: editFormState.type,
        },
      },
    });
    setEditingQuestionId(null);
  };

  const handleDeleteClick = (question: any) => {
    setQuestionToDelete(question);
    deleteConfirmation.setOpen(true);
  };

  if (isLoadingQuestions) {
    return (
      <MDBox display="flex" justifyContent="center" p={3}>
        <MDTypography variant="body2">Loading questions...</MDTypography>
      </MDBox>
    );
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              <TableCell align="left">
                <MDTypography variant="body2" fontWeight="medium">
                  Question
                </MDTypography>
              </TableCell>
              <TableCell align="left">
                <MDTypography variant="body2" fontWeight="medium">
                  Type
                </MDTypography>
              </TableCell>
              <TableCell align="right">
                <MDTypography variant="body2" fontWeight="medium">
                  Actions
                </MDTypography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {questions?.map((question) => {
              const isEditing = editingQuestionId === question.id;
              return (
                <TableRow key={question.id}>
                  {isEditing ? (
                    <>
                      <TableCell>
                        <MDInput
                          value={editFormState.question}
                          onChange={(e) =>
                            setEditFormState((prev) => ({ ...prev, question: e.target.value }))
                          }
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <MDSelect
                          fullWidth
                          label="Type"
                          value={editFormState.type}
                          onChange={(e: any) =>
                            setEditFormState((prev) => ({
                              ...prev,
                              type: e.target.value as DailyFormInspectionQuestionType,
                            }))
                          }
                          options={[
                            { label: "True/False", value: DailyFormInspectionQuestionType.BOOLEAN },
                            { label: "Text", value: DailyFormInspectionQuestionType.TEXT },
                          ]}
                          name="type"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={handleSaveChanges} disabled={isUpdatingQuestion}>
                          <Icon color="success">save</Icon>
                        </IconButton>
                        <IconButton onClick={handleCancelEditing}>
                          <Icon color="error">cancel</Icon>
                        </IconButton>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell align="left">
                        <MDTypography variant="body2">{question.question}</MDTypography>
                      </TableCell>
                      <TableCell align="left">
                        <MDTypography variant="body2">
                          {question.type === DailyFormInspectionQuestionType.BOOLEAN
                            ? "True/False"
                            : capitalCase(question.type)}
                        </MDTypography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => handleStartEditing(question)}>
                          <Icon>edit</Icon>
                        </IconButton>
                        <IconButton onClick={() => handleDeleteClick(question)}>
                          <Icon color="error">delete</Icon>
                        </IconButton>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ActionConfirmDialog
        open={deleteConfirmation.open}
        setOpen={deleteConfirmation.setOpen}
        handleCancel={deleteConfirmation.onCancel}
        handleAccept={deleteConfirmation.onAccept}
        title="Delete Question"
        content={`Are you sure you want to delete the question "${questionToDelete?.question}"? This action cannot be undone.`}
        acceptText="Delete"
        cancelText="Cancel"
      />
    </>
  );
}

export default QuestionsList;
